import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  Container as UIFoundationsContainer,
  StoreMap as UIFoundationStoreMap,
  StoreMapProps as UIFoundationStoreMapProps,
  StoreLocationConfig,
  ContainerPositions,
  ButtonIconPositions,
  IconSizes,
  InputTextFieldVariantProps
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  GeoLocation,
  SortField,
  SortOrder,
  Store,
  StoreListParams
} from '@dcxwam/stores-sdk'
import type {
  ApplyFiltersCallback,
  SelectedFiltersByGroup
} from '../common/filter-types'
import { useIsSsr } from '../../../../utils/isSsr'
import StoreMapEditConfig from './StoreMapEditConfig'
import { StoreMapProps } from './types'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import StoreMapFilter from '../StoreMapFilter/StoreMapFilter'
import { CbxConfigType, PageConfig } from '../../../../utils/page-config'
import {
  radiusByZoom,
  mapPositionOptions,
  transformCAConfigsData,
  mapFilterGroupsToStoreMapFilterSubGroup,
  extractFilterCodes,
  fallbackDays,
  extractFiltersCodesWithFlatMap
} from './utils'
import { useUserLoginInfo } from '../../../../utils/use-user-login-info'
import {
  useStoreClusters,
  useStoreZoomTracking,
  useLocationSearch,
  useNoStoresFoundConfig
} from './hooks'
import {
  createStoresSDK,
  getStoreList
} from '../../../../services/stores/stores-sdk'
import {
  getCurrentDayOpenCloseMessage,
  getCategoryDetails,
  getFormattedAddress,
  calculateStoreDistance,
  buildDataLayerStoreObj
} from '../../StoreDetails/utils'
import {
  buildDataLayerFilterClick,
  parseAdditionalFilterParameters
} from '../common/utils'
import { pushDataLayer } from '../../../../utils/datalayer'
import { useUserLocation } from '../common/hooks/useUserLocation'
import { useStoreLocation } from '../common/hooks/useStoreLocation'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { useSafeSearchParams } from '../../../../utils/use-safe-search-params'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'

const StoreMap: FC<{ model: StoreMapProps }> = withComponentRefresh((props) => {
  const { model, cqPath } = props
  const { isUserLoggedIn } = useUserLoginInfo()
  const { fetchLocation, userLocation } = useUserLocation()
  const {
    storeLocatorConfig,
    storeMapFilter,
    storeImage,
    storeMapConfig,
    storeDetailsConfig,
    radiusLimit,
    ...rest
  } = model
  const isSsr = useIsSsr()

  const {
    platformBrands,
    additionalFilterParameters,
    storeLandingPagePath,
    googleMapsAPIKey,
    filterButtonLabel,
    filterSectionLabel,
    applyFilterLabel,
    resetFilterLabel,
    distanceLabel
  } = storeLocatorConfig || {}

  const { labels, configs } = transformCAConfigsData({
    storeLocatorConfig,
    storeMapConfig,
    storeDetailsConfig
  })

  const {
    listViewLabel,
    listViewLmaoViewLabel,
    storeDetailsLabelOnListView,
    getDirectionsLabelOnListView,
    inputPlaceholder,
    currentLocationLabel,
    currentLocationAriaLabel,
    zoomInAriaLabel,
    zoomOutAriaLabel,
    openedUntilLabel,
    willOpenAtLabel
  } = labels

  const {
    storeTypeOrder,
    enableMilesAsDistanceUnit,
    initialRadiusValue,
    storeInfoUse,
    storeAddressFormatTemplate,
    categories,
    days,
    enable12HourFormat,
    maxZoomOutLevel
  } = configs

  const editableProps = useExtractEditableProps(props, rest)
  const editablePropsFilter = {
    ...props,
    cqPath: `${cqPath}/store-map-filter`,
    model: { ...storeMapFilter }
  }
  const [isAutomaticZoomOut, setIsAutomaticZoomOut] = useState(false)
  const [hasUserZoomed, setHasUserZoomed] = useState(false)

  const [servicesListAndStoreTypeMemo, setServicesListAndStoreTypeMemo] =
    useState<{
      servicesList: string[]
      storeInfoType: string[]
      selectedOpening: string[]
    }>({
      servicesList: [],
      storeInfoType: [],
      selectedOpening: []
    })

  const [searchParams, setSearchParams] = useSafeSearchParams()
  const isAuthor = AuthoringUtils.isInEditor()
  const { cbxConfig, lang, market } = PageConfig.getConfig() as {
    cbxConfig: CbxConfigType
    lang: string
    market: string
  }
  const [selectedStoreDetails, setSelectedStoreDetails] =
    useState<StoreLocationConfig | null>(null)
  const [nearByStores, setNearByStores] = useState<Store[]>([])
  const locationSearchQuery = searchParams.get('query')

  const urlLatitude = Number(searchParams.get('lat'))
  const urlLongitude = Number(searchParams.get('lng'))

  // Resolve the initial map center via the shared Store Locator location chain
  // (DCXWAM-41590): URL search params take precedence; otherwise fall back to
  // the "My Location" cookie coords (US market). StoreMap deliberately does NOT
  // use IP-based geolocation, so `enableIpFallback` is false. Downstream props
  // expect numbers defaulting to 0 when no location is resolvable.
  const { latitude: resolvedLatitude, longitude: resolvedLongitude } =
    useStoreLocation({
      urlCoords: { latitude: urlLatitude, longitude: urlLongitude },
      enableIpFallback: false
    })

  const searchLocationLatitude = resolvedLatitude ?? 0
  const searchLocationLongitude = resolvedLongitude ?? 0

  const { zoom, handleZoomIn, handleZoomOut, resetZoomTracking, setZoom } =
    useStoreZoomTracking({
      initialRadiusValue,
      isAutomaticZoomOut,
      setHasUserZoomed
    })

  const {
    storeLocations,
    isLoadingStores,
    initialStoreCount,
    fetchStoreClusters,
    resetStoreState
  } = useStoreClusters({
    zoom,
    setZoom,
    setHasUserZoomed,
    cbxConfig,
    searchLocationLatitude,
    searchLocationLongitude,
    platformBrands,
    additionalFilterParameters,
    servicesListAndStoreTypeMemo,
    storeInfoUse,
    lang,
    storeImage,
    storeLocatorConfig,
    storeDetailsConfig,
    storeTypeOrder,
    enableMilesAsDistanceUnit,
    isSsr,
    isUserLoggedIn,
    storeLandingPagePath,
    isAuthor,
    hasUserZoomed,
    isAutomaticZoomOut,
    setIsAutomaticZoomOut,
    radiusLimit
  })

  const { handleLocationSearchChange, handleCurrentLocation, handleDragEnd } =
    useLocationSearch({
      setSearchParams,
      currentLocationLabel,
      initialLatitude: searchLocationLatitude,
      initialLongitude: searchLocationLongitude,
      initialQuery: locationSearchQuery,
      searchParams,
      onLocationChange: () => {
        setIsAutomaticZoomOut(true)
        resetZoomTracking()
        resetStoreState()
      }
    })

  const noStoresFoundConfig = useNoStoresFoundConfig({
    isLoadingStores,
    storeLocations,
    initialStoreCount,
    zoom,
    noStoresMsg: model.noStoresMsg,
    noStoreTitle: model.noStoreTitle,
    nearStoresMsg: model.nearStoresMsg,
    storeLandingPageButtonLabel: model.storeLandingPageButtonLabel,
    locationSearchQuery
  })

  useEffect(() => {
    if (!cbxConfig || isAuthor || !selectedStoreDetails) {
      return
    }

    const fetchNearByStores = async () => {
      const {
        cbxUrl: baseCbxUrl,
        market: cbxMarket,
        applicationId,
        loggerEnabled
      } = cbxConfig

      const params: StoreListParams = {
        ...parseAdditionalFilterParameters(additionalFilterParameters),
        latitude: Number(selectedStoreDetails.centroid.latitude),
        longitude: Number(selectedStoreDetails.centroid.longitude),
        distance: 500,
        sortField: SortField.DISTANCE,
        sortOrder: SortOrder.ASC,
        ...(model.carouselStoreTypesList && {
          storeInfoType: model.carouselStoreTypesList
        }),
        ...(model.carouselCardCount && { pageSize: model.carouselCardCount }),
        ...(platformBrands && { platformBrands: platformBrands.split(',') })
      }

      const response = await getStoreList(
        createStoresSDK({
          baseCbxUrl,
          market: cbxMarket,
          lang,
          applicationId,
          loggerEnabled
        }),
        params
      )

      if (
        response &&
        'items' in response &&
        Array.isArray(response.items) &&
        response.items.length > 0
      ) {
        setNearByStores(response.items)
      }
    }
    if (
      selectedStoreDetails.type &&
      model.carouselStoreTypesMain?.includes(selectedStoreDetails.type)
    ) {
      fetchNearByStores()
    }
  }, [selectedStoreDetails, additionalFilterParameters])

  const storeCards = useMemo(() => {
    return nearByStores.map((store): StoreLocationConfig => {
      const category = getCategoryDetails(categories ?? [], store?.type ?? '')
      const currentDayInfo = getCurrentDayOpenCloseMessage(
        store?.openingHours ?? [],
        {
          labels: {
            openedUntilLabel: openedUntilLabel ?? '',
            willOpenAtLabel: willOpenAtLabel ?? ''
          },
          days: days ?? fallbackDays,
          enable12HourFormat
        }
      )
      const storeLocation = {
        latitude: store?.address?.location?.latitude ?? 0,
        longitude: store?.address?.location?.longitude ?? 0
      }

      const distance =
        store?.address?.location &&
        calculateStoreDistance(
          storeLocation,
          {
            latitude: searchLocationLatitude,
            longitude: searchLocationLongitude
          },
          {
            enableMilesAsDistanceUnit,
            isSsr
          }
        )
      const distanceValue = distance ? `(${distance})` : undefined

      return {
        storeImage,
        currentDay: currentDayInfo,
        address: {
          addressLine1: store.address?.addressLine1 ?? '',
          postalCode: store.address?.postalCode ?? '',
          city: store.address?.city ?? '',
          countryCode: store.address?.countryCode ?? '',
          formattedAddress: getFormattedAddress(
            store.address,
            storeAddressFormatTemplate
          ),
          location: storeLocation
        },
        distance: distanceValue,
        categoryIcon: category?.image ?? '',
        categoryLabel: '',
        phone: '',
        name: store?.name ?? '',
        type: store.type ?? '',
        storeCount: 1,
        isOfficialCluster: false,
        centroid: storeLocation,
        storePinSvgUrl: category?.image ?? ''
      }
    })
  }, [
    nearByStores,
    storeImage,
    categories,
    enableMilesAsDistanceUnit,
    openedUntilLabel,
    willOpenAtLabel,
    days,
    enable12HourFormat,
    storeAddressFormatTemplate
  ])
  const mapFilters = mapFilterGroupsToStoreMapFilterSubGroup(
    storeMapFilter?.filterGroups ?? []
  )
  const replaceWithStoreDistance = useCallback(
    (storeLocation?: GeoLocation) => {
      const storeDistance = calculateStoreDistance(
        storeLocation,
        userLocation,
        {
          enableMilesAsDistanceUnit,
          isSsr
        }
      )
      return distanceLabel && storeDistance
        ? `<b>${distanceLabel.replace('$distance', storeDistance)}</b>`
        : ''
    },
    [userLocation, enableMilesAsDistanceUnit, isSsr]
  )

  const handleDirectionsClick = useCallback((store: StoreLocationConfig) => {
    pushDataLayer(buildDataLayerStoreObj('StoreLocatorDirections', store))
  }, [])

  const handleStoreDetailsClick = useCallback((store: StoreLocationConfig) => {
    pushDataLayer(buildDataLayerStoreObj('storeLocatorDetailsClick', store))
  }, [])

  const storeMapConfigs: UIFoundationStoreMapProps = useMemo(
    () => ({
      mapConfig: {
        apiKey: isAuthor ? '' : (googleMapsAPIKey ?? ''),
        language: lang,
        region: market?.toUpperCase(),
        positionOptions: mapPositionOptions
      },
      userLocation,
      userLocationSvgUrl: storeLocatorConfig?.mapUserIcon,
      fetchLocation,
      storeLocations,
      zoom,
      changeCenterOnDrag: true,
      ...(storeMapFilter &&
        !storeMapFilter.hideFilters && {
          quickFiltersComponentId: storeMapFilter.quickFiltersComponentId,
          quickFilters: storeMapFilter.quickFilters,
          storeFilterConfig: {
            filters: mapFilters,
            allFiltersButtonLabel: filterButtonLabel ?? '',
            storeMapFilterTitle: filterSectionLabel ?? '',
            applyFiltersButtonLabel: applyFilterLabel ?? '',
            resetButtonLabel: resetFilterLabel ?? '',
            onFiltersApply: (filters: ApplyFiltersCallback): void => {
              const selectedFiltersByGroup =
                filters?.selectedFiltersByGroup ?? []

              // Extract filters by groupId
              const storeTypeGroup = selectedFiltersByGroup.find(
                (group: SelectedFiltersByGroup) =>
                  group.groupId === 'store-type'
              )
              const storeOpeningGroup = selectedFiltersByGroup.find(
                (group: SelectedFiltersByGroup) =>
                  group.groupId === 'store-opening'
              )
              const regularFilters = selectedFiltersByGroup.filter(
                (group: SelectedFiltersByGroup) =>
                  group.groupId !== 'store-type' &&
                  group.groupId !== 'store-opening'
              )

              const servicesList = regularFilters.flatMap(
                (group: SelectedFiltersByGroup) =>
                  extractFilterCodes(group.selectedOptions)
              )
              const storeInfoType = storeTypeGroup
                ? extractFiltersCodesWithFlatMap(storeTypeGroup.selectedOptions)
                : []
              const selectedOpening = storeOpeningGroup
                ? extractFilterCodes(storeOpeningGroup.selectedOptions)
                : []

              setServicesListAndStoreTypeMemo({
                servicesList,
                storeInfoType,
                selectedOpening
              })
              fetchStoreClusters({
                servicesList,
                storeInfoType,
                selectedOpening
              })
            },
            onFiltersReset() {
              setServicesListAndStoreTypeMemo({
                servicesList: [],
                storeInfoType: [],
                selectedOpening: []
              })
              fetchStoreClusters({
                servicesList: [],
                storeInfoType: [],
                selectedOpening: []
              })
            },
            onFilterClick: (data: {
              groupName: string
              filterCode: string
              filterLabel: string
            }) => {
              pushDataLayer(buildDataLayerFilterClick(data))
            }
          }
        }),
      noStoresFoundConfig,
      onStoreLandingPageClick: () => {
        const url = model.storeLandingPageUrl
        if (url) {
          window.open(url, '_self')
        }
      },
      searchLocation: {
        latitude: searchLocationLatitude,
        longitude: searchLocationLongitude
      },
      minZoom: maxZoomOutLevel ?? 8,
      maxZoom: radiusByZoom.length - 1,
      onZoomIn: handleZoomIn,
      onZoomOut: handleZoomOut,
      shareGeoLocation: handleDragEnd,
      onCurrentLocation: handleCurrentLocation,
      ...(listViewLabel && {
        showListViewButton: {
          label: listViewLabel,
          icon: 'pmi-icon-menu',
          iconPosition: ButtonIconPositions.Left
        }
      }),
      ...(listViewLmaoViewLabel && {
        showMapViewButton: {
          label: listViewLmaoViewLabel,
          icon: 'pmi-icon-location',
          iconPosition: ButtonIconPositions.Left
        }
      }),
      chevronIconProps: {
        image: 'pmi-icon-chevronright',
        size: IconSizes.Medium
      },
      scrollToMapProps: {
        scrollToMapLabel: model.storeMapConfig?.scrollDowLabel ?? '',
        scrollToMapIcon: 'pmi-icon-chevrondown'
      },
      storeDetailsLinkConfigOnStoreMap: {
        label: model.storeMapConfig?.storeDetailsLabel ?? '',
        icon: 'pmi-icon-store'
      },
      directionsLinkOnStoreMap: {
        label: model.storeMapConfig?.getDirectionLabel ?? '',
        icon: 'pmi-icon-direction'
      },
      ...(getDirectionsLabelOnListView && {
        directionsLink: {
          label: getDirectionsLabelOnListView
        }
      }),
      ...(storeDetailsLabelOnListView && {
        storeDetailsLink: {
          label: storeDetailsLabelOnListView
        }
      }),
      locationSearchConfig: {
        fullWidth: true,
        noOptionsText: '',
        variant: InputTextFieldVariantProps.Outlined,
        placeholder: inputPlaceholder,
        disableClearable: false,
        disableChevronIcon: true,
        defaultValue: locationSearchQuery ?? '',
        onChange: (
          _event: SyntheticEvent<Element, Event>,
          newValue: string | object | null
        ) => {
          handleLocationSearchChange(newValue)
        }
      },
      currentLocationAriaLabel,
      storeLocationDistanceLabel: distanceLabel,
      getStoreDistanceContent: replaceWithStoreDistance,
      zoomInAriaLabel,
      zoomOutAriaLabel,
      onDirectionsClick: handleDirectionsClick,
      onStoreDetailsClick: handleStoreDetailsClick,
      ...(model.promotionTitle && {
        storeMapPromotionBanner: {
          bannerTitle: model.promotionTitle,
          promotionProductName: model.promotionText,
          promotionStoreTypes: model.promotionStoreTypes,
          promotionBannerBackgroundImage:
            model.promotionBgImageModel?.image?.src,
          promotionImage: model.promotionProductImageModel?.image?.src,
          promotionTypeLabel: model.promotionTypeLabel,
          promotionTypeFontColor: model.promotionTypeFontColor,
          promotionTypeBackgroundColor: model.promotionTypeBackgroundColor,
          showDarkTheme: model.promotionShowDarkTheme
        }
      }),
      ...(model.carouselTitle && {
        storeMapNearByStoresPromotion: {
          carouselStoreTypesMain: model.carouselStoreTypesMain,
          maxStoresToShowInPromotionCarousel: model.carouselCardCount,
          nearStorePromotionCarouselTitle: model.carouselTitle,
          stores: storeCards
        }
      }),
      setSelectedStoreDetails: (store: StoreLocationConfig | null) => {
        setSelectedStoreDetails(store)
      }
    }),
    [
      zoom,
      storeLocations,
      handleZoomOut,
      handleZoomIn,
      isAutomaticZoomOut,
      hasUserZoomed,
      noStoresFoundConfig,
      searchLocationLatitude,
      searchLocationLongitude,
      locationSearchQuery,
      model,
      storeCards,
      maxZoomOutLevel
    ]
  )

  const containerProps = {
    position: ContainerPositions.Relative
  }

  return (
    <EditableComponent {...editableProps}>
      {isAuthor ? (
        <>
          {StoreMapEditConfig.configLabel}
          <StoreMapFilter {...editablePropsFilter} />
        </>
      ) : (
        <UIFoundationsContainer {...containerProps}>
          <UIFoundationStoreMap {...storeMapConfigs} />
        </UIFoundationsContainer>
      )}
    </EditableComponent>
  )
})

const StoreMapWithErrorBoundary = withErrorBoundary(StoreMap, {
  componentName: 'StoreMap',
  fallback: <ErrorFallback componentName="StoreMap" />
})

export default StoreMapWithErrorBoundary
